import { createContext, Dispatch, useCallback, useContext, useEffect, useState } from "react";
import { ResponseTypes } from "../services/APIService";
import AlertDialog from "../components/popupComp/AlertDialog";
import { ThemeTypes } from "./ContextTypes";
import { useTheme } from "./ThemeContext";
import { scheduleAutoLogout } from "../utils/keycloakPKCEUtils";

type ResponseType = {
    username: string;
    department: string;
    organization: string;
    policies: string[];
    contact: string;
    privileges: string[];
    theme: string;
    accentColor: string;
    firstName: string;
    areas: string[];
    roleType: string;
    role: string;
}

type UserDataContextType = {
    policies: PolicyTypes[];
    loading: boolean;
    hasPolicy: (policy: PolicyTypes) => boolean;
    hasAnyPolicy: (policies: PolicyTypes[]) => boolean;
    hasAllPolices: (policies: PolicyTypes[]) => boolean;
    hasPrivilege: (policy: PrivilegeTypes) => boolean;
    hasAnyPrivilege: (policies: PrivilegeTypes[]) => boolean;
    hasAllPrivileges: (policies: PrivilegeTypes[]) => boolean;
    department: string;
    organization: string;
    username: string;
    contact: string;
    userTheme: ThemeTypes;
    assentColor: string;
    firstName: string;
    isPolicyChanged: boolean;
    areas: string[];
    roleType: string
    //refetch: () => Promise<void>;
    setRowPolicies: Dispatch<React.SetStateAction<string[]>>,
    role: string
}

// Default values
const UserDataContext = createContext<UserDataContextType>({
    policies: [],
    loading: true,
    hasPolicy: () => false,
    hasAnyPolicy: () => false,
    hasAllPolices: () => false,
    hasPrivilege: () => false,
    hasAnyPrivilege: () => false,
    hasAllPrivileges: () => false,
    department: "",
    organization: "",
    username: "",
    contact: "",
    userTheme: 'colorful',
    assentColor: 'blue',
    firstName: "",
    isPolicyChanged: false,
    areas: [],
    roleType: "",
    //refetch: async () => {}
    setRowPolicies: () => {},
    role: ""
})

export type PolicyTypes = "ORGANIZATION_MANAGE" | "DEPARTMENT_MANAGE" | "WORKFLOW_MANAGE" | "USER_MANAGE" | "WEATHER" | "EARLY_WARNING" | "REPORTED_INCIDENTS" | "AIR_QUALITY" | "SATELLITE" | "PARTICLES" | "RAINFALL" | "RAINFALL" | "RIVER_SENSOR" | "SIMPLE_EARLY_WARNING_TRIGGER" | "ADVANCED_EARLY_WARNING_TRIGGER" | "BOTH_EARLY_WARNING_TRIGGER_SIMPLE" | "BOTH_EARLY_WARNING_TRIGGER_ADVANCED" | "EARTHQUAKE" | "SETUP_EARLY_WARNING_TEMPLATE" | "ACKNOWLEDGEMENT" | "SETUP_RECIPIENT" | "INCIDENT_MANGE" | "AWARENESS_MANAGE" | "SETUP_SUBSCRIBER" | "SETUP_GROUP" | "FLOOD_SIMULATION" | "TSUNAMI_SIMULATION" | "STORM_SURGE_SIMULATION" | "CYCLONE_SIMULATION" | "THEME_SETTING" | "PLUGIN_SETTING" | "MASSAGE_CONFIG" | "EARLY_WARNING_MESSAGE" | "USER_ROLE_MANGE"

const policyMap = new Map<string, PolicyTypes>([
    ["Organization Manage", "ORGANIZATION_MANAGE"],
    ["Department Manage", "DEPARTMENT_MANAGE"],
    ["Workflow Manage", "WORKFLOW_MANAGE"],
    ["User Manage", "USER_MANAGE"],
    ["Weather", "WEATHER"],
    ["Early Warning", "EARLY_WARNING"],
    ["Reported Incidents", "REPORTED_INCIDENTS"],
    ["Air Quality", "AIR_QUALITY"],
    ["Satellite", "SATELLITE"],
    ["Particales", "PARTICLES"],
    ["Rainfall", "RAINFALL"],
    ["River Sensors", "RIVER_SENSOR"],
    ["Simple Early Warning Trigger", "SIMPLE_EARLY_WARNING_TRIGGER"],
    ["Advance Early Warning Trigger", "ADVANCED_EARLY_WARNING_TRIGGER"],
    ["Both Early Warning Trigger - Simple", "BOTH_EARLY_WARNING_TRIGGER_SIMPLE"],
    ["Both Early Warning Trigger - Advance", "BOTH_EARLY_WARNING_TRIGGER_ADVANCED"],
    ["Earthquake", "EARTHQUAKE"],
    ["Setup Early Warning Template", "SETUP_EARLY_WARNING_TEMPLATE"],
    ["Acknowledgement", "ACKNOWLEDGEMENT"],
    ["Setup Recipients", "SETUP_RECIPIENT"],
    ["Incident Manage", "INCIDENT_MANGE"],
    ["Awareness Manage", "AWARENESS_MANAGE"],
    ["Setup Subscriber", "SETUP_SUBSCRIBER"],
    ["Setup Group", "SETUP_GROUP"],
    ["Flood Simulation", "FLOOD_SIMULATION"],
    ["Tsunami Simulation", "TSUNAMI_SIMULATION"],
    ["Storm Surge Simulation", "STORM_SURGE_SIMULATION"],
    ["Theme Setting", "THEME_SETTING"],
    ["Plugin Setting", "PLUGIN_SETTING"],
    ["Message Configuration", "MASSAGE_CONFIG"],
    ["Early Warning Message", "EARLY_WARNING_MESSAGE"],
    ["User Role Manage", "USER_ROLE_MANGE"],
    ["Cyclone Simulation", "CYCLONE_SIMULATION"]
])

type PrivilegeTypes = "BCONX_BACKOFFICE" | "BCONX_MOBILE" | "EAGLE-I"

const privilegeMap = new Map<string, PrivilegeTypes>([
    ["bconex_backoffice", "BCONX_BACKOFFICE"],
    ["bconex_mobile", "BCONX_MOBILE"],
    ["eagle-i", "EAGLE-I"],
])

const getToken = (): string | null => {
    const token = localStorage.getItem("access_token");
    return token
}

let lastPolicy: PolicyTypes[] = []

// Provide component
export const UserDataProvider = ({ children }: {children: React.ReactNode}) => {

    const BASE_URL = process.env.REACT_APP_BASE_URL
    const POLICY_UPDATE_INTERVAL: number = process.env.REACT_APP_POLICY_UPDATE_INTERVAL ? Number(process.env.REACT_APP_POLICY_UPDATE_INTERVAL) : 10000

    const { setTheme } = useTheme()

    const [policies, setPolices] = useState<PolicyTypes[]>([])
    const [privileges, setPrivileges] = useState<PrivilegeTypes[]>([])

    const [loading, setLoading] = useState<boolean>(true)

    const [department, setDepartment] = useState<string>("")
    const [organization, setOrganization] = useState<string>("")
    const [username, setUsername] = useState<string>("")
    const [contact, setContact] = useState<string>("")
    const [userTheme, setUserTheme] = useState<ThemeTypes>("colorful")
    const [assentColor, setAccentColor] = useState<string>("")
    const [firstName, setFirstName] = useState<string>("")
    const [areas, setAreas] = useState<string[]>([])
    const [roleType, setRoleType] = useState<string>("")
    const [role, setRole] = useState<string>("")

    const [isPolicyChanged, setIsPolicyChanged] = useState<boolean>(false)

    const [alertDialogState, setAlertDialogState] = useState(false);
    const [alertTitle, setAlertTitle] = useState('');
    const [alertDescription, setAlertDescription] = useState('');
    const [alertYesCallback, setAlertYesCallback] = useState<(() => void) | undefined>(undefined); // for Yes
    const [alertOkCallback, setAlertOkCallback] = useState<(() => void) | undefined>(undefined);  // for OK

    const showAlertDialog = (title: string, description: string, yesCallback?: () => void, okCallback?: () => void ) => {
        setAlertTitle(title);
        setAlertDescription(description);
        setAlertYesCallback(() => yesCallback); // Will show Yes/Cancel if defined
        setAlertOkCallback(() => okCallback);   // Will trigger only on OK
        setAlertDialogState(true);
    }

    const [rowPolices, setRowPolicies] = useState<string[]>([])

    //Fetch polices from backend
    const fetchPolices = useCallback(async () => {

        const token = getToken()

        if (!token) return
        try
        {
            setLoading(true)
            //scheduleAutoLogout()

            //const URL: string = `${BASE_URL}/identity_manager/api/v1/getUserPolices`
            const URL: string = `${BASE_URL}/identity_manager/api/v1/getUserDetails`
            const HEADER: RequestInit = {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }

            const request = await fetch(URL, HEADER)

            if (!request.ok) throw new Error("Failed to fetch polices")
                
            const data = await request.json() as ResponseTypes
            const dataList: ResponseType = data.data as ResponseType
            //console.log("POLICY ROW -> ", dataList)
            const availablePolices: PolicyTypes[] = []
            //console.log("POLICES -> ", dataList.policies)
            dataList.policies.map(item => {
                if (item)
                {
                    availablePolices.push(policyMap.get(item)!)
                }
            })

            if (JSON.stringify(availablePolices) !== JSON.stringify(lastPolicy))
            {
                setIsPolicyChanged(prevState => !prevState)
            }
            lastPolicy = availablePolices
            setPolices(availablePolices)

            const availablePrivileges: PrivilegeTypes[] = []
            dataList.privileges.map(item => {
                if (item)
                {
                    availablePrivileges.push(privilegeMap.get(item)!)
                }
            })
            setPrivileges(availablePrivileges)
            
            setDepartment(dataList.department)
            setOrganization(dataList.organization)
            setUsername(dataList.username)
            setContact(dataList.contact)
            setUserTheme(dataList.theme as ThemeTypes)
            setAccentColor(dataList.accentColor)
            setFirstName(dataList.firstName)
            setAreas(dataList.areas)
            setRoleType(dataList.roleType)
            setTheme(dataList.theme as ThemeTypes)
            setRole(dataList.role)
            localStorage.setItem("theme", dataList.theme)

            //console.log("user data updated")
        }
        catch (error)
        {
            console.log("Error fetching polices: ", error)
            // sessionStorage.clear()
            // localStorage.clear()
            //showAlertDialog("Error", "❌ Unable to connect server", undefined, () => window.location.href = "/login");
            //showAlertDialog("Error", "Unable to connect server");
            setPolices([])
        }
        finally 
        {
            setLoading(false)
        }
    }, [BASE_URL])

    // useEffect(() => {
    //     console.log("POLICES -> ", policies)
    // }, [policies])
    // const fetchPolices = useCallback(() => {
    //     console.log("ROW POLICES -> ", rowPolicies)


    //     // setDepartment(dataList.department)
    //     // setOrganization(dataList.organization)
    //     // setUsername(dataList.username)
    //     // setContact(dataList.contact)
    //     // setUserTheme(dataList.theme as ThemeTypes)
    //     // setAccentColor(dataList.accentColor)
    //     // setFirstName(dataList.firstName)
    // }, [rowPolicies])

    // Trigger very 10 second
    useEffect(() => {
        scheduleAutoLogout()
        fetchPolices()
        const interval = setInterval(scheduleAutoLogout, POLICY_UPDATE_INTERVAL)
        
        return () => clearInterval(interval)
    }, [fetchPolices, POLICY_UPDATE_INTERVAL])

    // Permission checker
    const hasPolicy = useCallback((policy: PolicyTypes) => policies.includes(policy), [policies])

    const hasAnyPolicy = useCallback((required: PolicyTypes[]) => required.some((p) => policies.includes(p)), [policies])

    const hasAllPolices = useCallback((required: PolicyTypes[]) => required.every((p) => policies.includes(p)), [policies])

    // Privilege checker
    const hasPrivilege = useCallback((privilege: PrivilegeTypes) => privileges.includes(privilege), [privileges])

    const hasAnyPrivilege = useCallback((required: PrivilegeTypes[]) => required.some((p) => privileges.includes(p)), [privileges])

    const hasAllPrivileges = useCallback((required: PrivilegeTypes[]) => required.every((p) => privileges.includes(p)), [privileges])

    return (
        <UserDataContext.Provider value={{
            policies,
            loading,
            hasPolicy,
            hasAnyPolicy,
            hasAllPolices,
            department,
            organization,
            username,
            contact,
            roleType,
            hasPrivilege,
            hasAnyPrivilege,
            hasAllPrivileges,
            userTheme,
            assentColor,
            firstName,
            isPolicyChanged,
            areas,
            //refetch: fetchPolices,
            setRowPolicies,
            role
        }}>
            <>
                { children }
                {alertDialogState && (
                    <AlertDialog
                        title={alertTitle}
                        description={alertDescription}
                        windowState={alertDialogState}
                        setWindowState={setAlertDialogState}
                        function={alertYesCallback}
                        okFunction={alertOkCallback}
                    />
                )}
            </>
            
        </UserDataContext.Provider>
    )
}

// Custom hook for provide
export const useUserData = () => useContext(UserDataContext)